import { h, Component } from 'preact';
import { Text } from 'preact-i18n';
import { observer } from 'mobx-preact';

const SECTION_CONFIG = [
    {
        key: 'norwegianEvents',
        icon: 'fa-duotone fa-flag',
        title: <Text id='onThisDay.norwegianEvents'>Norsk historie</Text>,
    },
    {
        key: 'events',
        icon: 'fa-duotone fa-book',
        title: <Text id='onThisDay.events'>Hendelser</Text>,
    },
    {
        key: 'births',
        icon: 'fa-duotone fa-cake-candles',
        title: <Text id='onThisDay.births'>Fødsler</Text>,
    },
    {
        key: 'deaths',
        icon: 'fa-duotone fa-cross',
        title: <Text id='onThisDay.deaths'>Dødsfall</Text>,
    },
];

function formatClockParts(date) {
    return {
        hours: String(date.getHours()).padStart(2, '0'),
        minutes: String(date.getMinutes()).padStart(2, '0'),
    };
}

const CLOCK_STYLE = `
@keyframes onThisDayColonPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.25; }
}
.onThisDay-colon {
    animation: onThisDayColonPulse 1s ease-in-out infinite;
    display: inline-block;
    padding: 0 0.05em;
}
`;

function formatLongDate(date) {
    return date.toLocaleDateString('nb-NO', {
        day: 'numeric', month: 'long', year: 'numeric',
    });
}

function formatWeekday(date) {
    return date.toLocaleDateString('nb-NO', { weekday: 'long' });
}

function capitalize(s) {
    if (!s) return s;
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function getWeatherIcon(symbolCode) {
    if (!symbolCode) return 'fa-cloud';
    const code = String(symbolCode).toLowerCase();
    if (code.includes('snow') || code.includes('sleet')) return 'fa-snowflake';
    if (code.includes('rain') || code.includes('drizzle')) return 'fa-cloud-rain';
    if (code.includes('thunder')) return 'fa-cloud-bolt';
    if (code.includes('fog')) return 'fa-smog';
    if (code.includes('clearsky')) return 'fa-sun';
    if (code.includes('fair')) return 'fa-cloud-sun';
    if (code.includes('partlycloudy')) return 'fa-cloud-sun';
    if (code.includes('cloudy')) return 'fa-cloud';
    return 'fa-cloud';
}

function getWeatherIconColor(symbolCode) {
    if (!symbolCode) return 'text-body-secondary';
    const code = String(symbolCode).toLowerCase();
    if (code.includes('clearsky') || code.includes('fair') || code.includes('partlycloudy')) {
        return 'text-warning';
    }
    if (code.includes('thunder')) return 'text-warning';
    if (code.includes('rain') || code.includes('drizzle')) return 'text-info';
    if (code.includes('snow') || code.includes('sleet')) return 'text-info';
    return 'text-body-secondary';
}

function formatTemp(value) {
    if (value == null) return '';
    const rounded = Math.round(value);
    return `${rounded > 0 ? '+' : ''}${rounded}°`;
}

function pickSymbol(slot) {
    return slot?.next_1_hours?.summary?.symbol_code
        || slot?.next_6_hours?.summary?.symbol_code
        || slot?.next_12_hours?.summary?.symbol_code
        || null;
}

function formatHour(date) {
    return `${String(date.getHours()).padStart(2, '0')}:00`;
}

@observer
class OnThisDayCard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            now: new Date(),
            expanded: {},
            showMore: false,
        };
        this.clockTimer = null;
    }

    componentDidMount() {
        const { onThisDayStore } = this.props.stores;
        onThisDayStore.load();
        onThisDayStore.loadWeather();
        // Tick every second so the colon pulse stays in sync with real time
        // and the minute display updates without lag around the boundary.
        this.clockTimer = setInterval(this.tick, 1000);
        // Refresh weather every 30 minutes while the card is mounted.
        this.weatherTimer = setInterval(() => {
            onThisDayStore.loadWeather();
        }, 30 * 60 * 1000);
    }

    componentWillUnmount() {
        if (this.clockTimer) {
            clearInterval(this.clockTimer);
        }
        if (this.weatherTimer) {
            clearInterval(this.weatherTimer);
        }
    }

    tick = () => {
        this.setState({ now: new Date() });
    }

    toggleSection = (key) => {
        this.setState(prev => ({
            expanded: { ...prev.expanded, [key]: !prev.expanded[key] },
        }));
    }

    toggleShowMore = () => {
        this.setState(prev => ({ showMore: !prev.showMore }));
    }

    renderEntryList(entries, limit) {
        const shown = entries.slice(0, limit);
        return (
            <ul class='list-unstyled mb-0 ps-1'>
                {shown.map((entry, idx) => (
                    <li key={idx} class='mb-1' style='font-size: 0.9rem;'>
                        {entry.year != null && (
                            <span class='badge bg-body-secondary text-body-emphasis me-2' style='font-variant-numeric: tabular-nums;'>
                                {entry.year < 0 ? `${Math.abs(entry.year)} f.Kr.` : entry.year}
                            </span>
                        )}
                        <span class='text-body'>{entry.text}</span>
                    </li>
                ))}
            </ul>
        );
    }

    render() {
        const { stores } = this.props;
        const { onThisDayStore } = stores;
        const {
            onThisDay = {}, isLoading, weather = [], weatherLocation,
        } = onThisDayStore;
        const { now, expanded, showMore } = this.state;

        const currentWeather = weather?.[0] || null;
        const upcomingWeather = (weather || []).slice(1, 4);

        const weekday = capitalize(formatWeekday(now));
        const dateStr = formatLongDate(now);
        const { hours, minutes } = formatClockParts(now);

        const {
            nameDays = [],
            observances = [],
            events = [],
            norwegianEvents = [],
            births = [],
            deaths = [],
        } = onThisDay;

        const sections = {
            norwegianEvents,
            events,
            births,
            deaths,
        };

        const hasAnyData = nameDays.length > 0
            || observances.length > 0
            || events.length > 0
            || norwegianEvents.length > 0
            || births.length > 0
            || deaths.length > 0;

        const hasMoreSections = events.length > 0
            || norwegianEvents.length > 0
            || births.length > 0
            || deaths.length > 0
            || observances.length > 1;

        return (
            <div class='card mb-3 rounded-lg' style='min-width: 0; overflow: hidden;'>
                <div class='card-body py-2 px-3' style='line-height: 1.25; min-width: 0; overflow-wrap: anywhere; word-break: break-word;'>
                    <div class='d-flex align-items-center justify-content-between flex-wrap mb-1'>
                        <div>
                            <h5 class='card-title mb-0' style='line-height: 1.1;'>
                                <i class='fa-duotone fa-calendar-star me-2 text-primary' />
                                {weekday}
                            </h5>
                            <span class='text-body-secondary' style='font-size: 0.85rem;'>{dateStr}</span>
                        </div>
                        <div class='text-end'>
                            <div class='fw-bold' style='font-size: 1.75rem; font-variant-numeric: tabular-nums; line-height: 1;'>
                                {hours}<span class='onThisDay-colon'>:</span>{minutes}
                            </div>
                        </div>
                    </div>
                    <style>{CLOCK_STYLE}</style>

                    {nameDays.length > 0 && (
                        <div class='mb-1'>
                            <span class='text-body-secondary me-2' style='font-size: 0.85rem;'>
                                <i class='fa-duotone fa-id-badge me-1' />
                                <Text id='onThisDay.nameDay'>Navnedag</Text>:
                            </span>
                            {nameDays.map((n, idx) => (
                                <span key={idx} class='badge bg-primary-subtle text-primary-emphasis me-1'>{n}</span>
                            ))}
                        </div>
                    )}

                    {observances.length > 0 && (
                        <div class='mb-1'>
                            {(showMore ? observances : observances.slice(0, 1)).map((o, idx) => (
                                <div key={idx} style='font-size: 0.9rem;'>
                                    <span class='text-body-secondary me-2' style='font-size: 0.85rem;'>
                                        <i class='fa-duotone fa-star me-1' />
                                        <Text id='onThisDay.observance'>Merkedag</Text>:
                                    </span>
                                    <span class='text-body'>{o}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {hasMoreSections && (
                        <div class='mb-1'>
                            <button
                                type='button'
                                class='btn btn-link btn-sm p-0 text-body-secondary text-decoration-none'
                                style='font-size: 0.85rem;'
                                onClick={this.toggleShowMore}
                            >
                                {showMore ? (
                                    <Text id='onThisDay.showLess'>Vis mindre</Text>
                                ) : (
                                    <Text id='onThisDay.showMore'>Vis mer</Text>
                                )}
                                <i class={`fa-solid fa-chevron-${showMore ? 'up' : 'down'} ms-1`} />
                            </button>
                        </div>
                    )}

                    {currentWeather && (
                        <div class='pt-1 mt-1 border-top d-flex align-items-center justify-content-center justify-content-lg-between flex-wrap' style='gap: 0.75rem 1.25rem; min-width: 0;'>
                            <div class='d-flex align-items-center flex-wrap justify-content-center' style='gap: 0.75rem; min-width: 0;'>
                                <div class='d-flex align-items-center'>
                                    <i
                                        class={`fa-duotone ${getWeatherIcon(pickSymbol(currentWeather))} ${getWeatherIconColor(pickSymbol(currentWeather))} me-2`}
                                        style='font-size: 1.5rem;'
                                    />
                                    <div class='d-flex align-items-baseline' style='gap: 0.4rem;'>
                                        <span class='fw-bold' style='font-size: 1.1rem; font-variant-numeric: tabular-nums; line-height: 1;'>
                                            {formatTemp(currentWeather.instant?.details?.air_temperature)}
                                        </span>
                                        <span class='text-body-secondary' style='font-size: 0.75rem;'>
                                            {weatherLocation || <Text id='onThisDay.weatherNow'>Nå</Text>}
                                        </span>
                                    </div>
                                </div>
                                {currentWeather.instant?.details?.wind_speed != null && (
                                    <div class='text-body-secondary' style='font-size: 0.85rem;'>
                                        <i class='fa-duotone fa-wind me-1' />
                                        {Math.round(currentWeather.instant.details.wind_speed)} m/s
                                    </div>
                                )}
                            </div>
                            {upcomingWeather.length > 0 && (
                                <div class='d-flex align-items-center flex-wrap justify-content-center' style='gap: 0.75rem 1.25rem; min-width: 0;'>
                                    {upcomingWeather.map((slot, idx) => {
                                        const when = slot.date ? new Date(slot.date) : new Date(slot.time);
                                        const temp = slot.instant?.details?.air_temperature;
                                        const symbol = pickSymbol(slot);
                                        return (
                                            <div key={idx} class='d-flex align-items-center' style='gap: 0.35rem; font-size: 0.85rem;'>
                                                <span class='text-body-secondary' style='font-variant-numeric: tabular-nums;'>
                                                    {formatHour(when)}
                                                </span>
                                                <i class={`fa-duotone ${getWeatherIcon(symbol)} ${getWeatherIconColor(symbol)}`} />
                                                <span class='fw-semibold' style='font-variant-numeric: tabular-nums;'>
                                                    {formatTemp(temp)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {!hasAnyData && !isLoading && (
                        <p class='text-body-secondary mb-0' style='font-size: 0.9rem;'>
                            <Text id='onThisDay.noData'>Ingen data for denne dagen enda.</Text>
                        </p>
                    )}

                    {isLoading && !hasAnyData && (
                        <div class='d-flex align-items-center text-body-secondary' style='font-size: 0.9rem;'>
                            <div class='spinner-border spinner-border-sm me-2' role='status' />
                            <Text id='onThisDay.loading'>Henter dagens historikk...</Text>
                        </div>
                    )}
                </div>

                {hasAnyData && showMore && (
                    <ul class='list-group list-group-flush'>
                        {SECTION_CONFIG.map(section => {
                            const entries = sections[section.key] || [];
                            if (entries.length === 0) return null;
                            const isOpen = !!expanded[section.key];
                            return (
                                <li key={section.key} class='list-group-item px-3 py-2'>
                                    <button
                                        type='button'
                                        class='btn btn-link p-0 text-decoration-none text-body w-100 d-flex align-items-center justify-content-between'
                                        onClick={() => this.toggleSection(section.key)}
                                    >
                                        <span>
                                            <i class={`${section.icon} me-2`} />
                                            {section.title}
                                            <span class='badge bg-body-secondary text-body-emphasis ms-2'>{entries.length}</span>
                                        </span>
                                        <i class={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'} text-body-secondary`} />
                                    </button>
                                    {isOpen && (
                                        <div class='mt-2'>
                                            {this.renderEntryList(entries, 50)}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        );
    }
}

export default OnThisDayCard;
